import {
	useMutation,
	UseMutationResult,
	useQuery,
	useQueryClient,
	UseQueryResult,
} from 'react-query'
import {
	CustomerDocType,
	LoginCustomerType,
	LoginProviderType,
	ProviderDocType,
} from '@quikplow/quikplow-types'
import decodeJWT from 'jwt-decode'
import QueryString from 'qs'

import { useAppConfig } from '@components/providers/AppConfig'
import { AccountTypes } from '@components/providers/Auth/types'
import { get, patch, post } from '@root/api'
import { GenericStatusError } from '@root/types/errors'
import { canLoop } from '@utils/canLoop'
import { isResJSON } from '@utils/isResJSON'
import { secureStorage } from '@utils/storage'

export const fetchMeQueryKey = 'fetch-me'

type useUserByIDQueryProps = {
	accountType?: AccountTypes
	depth?: number
	refetchInterval?: number
}
export function useFetchMeQuery({
	accountType,
	refetchInterval,
}: useUserByIDQueryProps): UseQueryResult<ProviderDocType | CustomerDocType | null, unknown> {
	const queryClient = useQueryClient()
	const { setIsDemo } = useAppConfig()

	const queryRes = useQuery(
		[fetchMeQueryKey],
		async () => {
			const token = await secureStorage.getJWTToken()
			if (token) {
				let apiDomain = process.env.REACT_APP_API_URL
				const { email }: { email: string } = decodeJWT(token)
				if (email === 'demo@miquikplow.com') {
					apiDomain = process.env.REACT_APP_API_URL_DEMO
					setIsDemo(true)
				}
				const storageAccountType = await secureStorage.getAccountType()
				const attemptToLoginAs = accountType || storageAccountType
				if (attemptToLoginAs) {
					const res = await get(`${apiDomain}/api/${attemptToLoginAs}/me?depth=0`)
					if (res.status === 200) {
						const json = await res.json()
						if (json?.user?.accountStatus === 'deleted') {
							await post(`${apiDomain}/api/${attemptToLoginAs}/logout`)
							await secureStorage.setJWTToken('')
							queryClient.invalidateQueries(fetchMeQueryKey)
							queryClient.setQueryData(fetchMeQueryKey, null)
						}
						return json.user
					}
				}
			}
			return null
		},
		{
			refetchInterval,
			onError: () => {
				throw GenericStatusError({ status: 501, message: 'An unknown error has occurred' })
			},
		}
	)

	return queryRes
}

type MeMutationProps = {
	data: any
	accountType: AccountTypes
	depth?: number
	queryParams?: {
		[key: string]: any
	}
}
export function useMeMutation(): UseMutationResult<
	{ doc: ProviderDocType | CustomerDocType },
	unknown,
	MeMutationProps
> {
	const queryClient = useQueryClient()
	const { data: user } = useFetchMeQuery({})
	const { apiDomain } = useAppConfig()

	const mutation = useMutation(
		async ({ data, accountType, depth = 0, queryParams = {} }: MeMutationProps) => {
			const params = QueryString.stringify(
				{
					...queryParams,
					depth: depth || queryParams?.depth || 0,
				},
				{ addQueryPrefix: true }
			)

			if (user?.id) {
				const res = await patch(`${apiDomain}/api/${accountType}/${user?.id}${params}`, {
					body: JSON.stringify(data),
				})
				if (isResJSON(res)) {
					const json = await res.json()
					if (res.status === 200) {
						return json
					}
					if (res.status === 400) {
						if (canLoop(json.errors)) {
							throw GenericStatusError({
								status: 400,
								message: 'Validation errors occurred during the form submission',
								data: { ...json },
							})
						}
					}
				}

				throw GenericStatusError({
					status: 501,
					message: 'An unknown error occurred while updating the user.',
				})
			}

			throw GenericStatusError({
				status: 400,
				message: 'Could not find your users id.',
			})
		},
		{
			onSuccess: (data) => {
				if (data?.doc) {
					queryClient.invalidateQueries(fetchMeQueryKey)
					queryClient.setQueryData(fetchMeQueryKey, data.doc)
				}
			},
		}
	)

	return mutation
}

type MutateUserLoginProps = {
	data: {
		email: string
		password: string
	}
	accountType: AccountTypes
	apiDomain: string
}
export function useUserLoginMutation(): UseMutationResult<
	LoginProviderType | LoginCustomerType,
	unknown,
	MutateUserLoginProps
> {
	const queryClient = useQueryClient()

	const mutation = useMutation(
		async ({ data, accountType, apiDomain }: MutateUserLoginProps) => {
			const res = await post(`${apiDomain}/api/${accountType}/login?depth=0`, {
				body: JSON.stringify(data),
			})

			if (isResJSON(res)) {
				if (res.status === 200) {
					return res.json()
				}

				if (res.status === 401) {
					throw GenericStatusError({
						status: 401,
						message: 'There was a problem logging in with the credentials you provided.',
					})
				}
			}
			console.log(res)
			throw GenericStatusError({
				status: 501,
				message: 'An unknown error occurred when attempting to log you in',
			})
		},
		{
			onSuccess: async (data, { accountType }) => {
				await secureStorage.setJWTToken(data.token)
				await secureStorage.setAccountType(accountType)
				queryClient.invalidateQueries(fetchMeQueryKey)
				queryClient.setQueryData(fetchMeQueryKey, { ...data.user })
			},
		}
	)

	return mutation
}

export function useUserLogoutMutation(): UseMutationResult<null, unknown, void> {
	const queryClient = useQueryClient()
	const { apiDomain } = useAppConfig()

	const mutation = useMutation(
		async () => {
			const accountType = await secureStorage.getAccountType()
			const res = await post(`${apiDomain}/api/${accountType}/logout`)

			if (isResJSON(res)) {
				if (res.status === 200) {
					return res.json()
				}
			}

			throw GenericStatusError({
				status: 501,
				message: 'An unknown error occurred when attempting to log you out',
			})
		},
		{
			onMutate: async () => {
				await secureStorage.setJWTToken('')
				queryClient.invalidateQueries(fetchMeQueryKey)
				queryClient.setQueryData(fetchMeQueryKey, null)
			},
		}
	)

	return mutation
}

type MutateUserCreateProps = {
	data: any
	accountType: AccountTypes
}
export function useUserCreateMutation(): UseMutationResult<
	{ doc: LoginProviderType | LoginCustomerType },
	unknown,
	MutateUserCreateProps
> {
	const queryClient = useQueryClient()
	const { apiDomain } = useAppConfig()

	const mutation = useMutation(
		async ({ data, accountType }: MutateUserCreateProps) => {
			const res = await post(`${apiDomain}/api/${accountType}?depth=0`, {
				body: JSON.stringify(data),
			})

			if (isResJSON(res)) {
				if (res.status === 201) {
					const json = await res.json()
					await secureStorage.setJWTToken(json.doc.token)
					await secureStorage.setAccountType(accountType)
					return json
				}

				if (res.status === 401) {
					throw GenericStatusError({
						status: 401,
						message: 'There was a creating your account.',
					})
				}
			}

			throw GenericStatusError({
				status: 501,
				message: 'An unknown error occurred when attempting to create your account',
			})
		},
		{
			onSuccess: ({ doc }) => {
				queryClient.invalidateQueries(fetchMeQueryKey)
				queryClient.setQueryData(fetchMeQueryKey, doc)
			},
		}
	)

	return mutation
}
